import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { Iconify, qaProp } from '@mfw/components';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { useIntl } from 'react-intl';
import React from 'react';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';

import routeMessages, { type RoutesMessagesDescriptor } from '../../messages';
import { useBreadcrumbsContext } from '../../../context';
import { useCanReadClients } from '../../../hooks/users/useCanReadClients';
import appMessages from '../../../messages';
import messages from './messages';
import './Breadcrumbs.scss';

export interface BreadcrumbsProps {
  // TODO Make it required
  qa?: string;
}

/**
 * TODO Add types for handle.title or switch to handle.titleTemplate
 */
function isTemplateTitle(title?: string) {
  if (!title) return;
  return !!title.startsWith(':');
}

const Breadcrumbs = ({ qa }: BreadcrumbsProps) => {
  const { formatMessage } = useIntl();
  const { matches: breadCrumbs } = useBreadcrumbsContext();
  const location = useLocation();
  const organizationId = matchPath(
    '/organizations/:organizationId/*',
    location.pathname,
  )?.params.organizationId;
  const canReadClients = useCanReadClients(organizationId);
  // Only matches that carry a title are rendered, so "which one is the
  // current page" has to be measured on the RENDERED list: a route whose
  // deepest matches are untitled (a layout segment, or the index route that
  // renders the page) otherwise leaves every crumb — including the current
  // one — rendered as a link to itself.
  const renderedCrumbs = breadCrumbs
    .filter((breadcrumb) => Boolean(breadcrumb.title))
    .map((breadcrumb) => {
      const to = breadcrumb.to ?? breadcrumb.pathname;
      const isClientsList =
        to !== false &&
        matchPath('/organizations/:organizationId/clients', to.split('?')[0]);
      return {
        ...breadcrumb,
        to: isClientsList && !canReadClients ? (false as const) : to,
      };
    });

  // Just "Home" carries no trail worth showing.
  if (renderedCrumbs.length === 0) return null;
  // A route that renders its own back link (e.g. a form page reached from a
  // single tabbed hub) opts out of the app-wide trail entirely.
  if (breadCrumbs.some((breadcrumb) => breadcrumb.hideBreadcrumbs)) return null;

  const lastPathIndex = renderedCrumbs.length - 1;
  // A deep trail offers one level up only. Without an accessible parent
  // destination, omit back rather than jump to Home or another ancestor.
  let backTo =
    renderedCrumbs.length >= 2
      ? renderedCrumbs[renderedCrumbs.length - 2].to || undefined
      : undefined;
  // Only the same organization's clients list may supply a return query string.
  // Router state is untrusted and may survive an organization switch.
  const state: unknown = location.state;
  const from =
    state && typeof state === 'object' && 'from' in state
      ? state.from
      : undefined;
  if (
    backTo &&
    matchPath(
      '/organizations/:organizationId/clients/:userId',
      location.pathname,
    ) &&
    backTo === `/organizations/${organizationId}/clients` &&
    typeof from === 'string' &&
    (from === backTo || from.startsWith(`${backTo}?`))
  ) {
    backTo = from;
  }

  return (
    <Box alignItems="center" display="flex" gap={0.5} minWidth={0}>
      {backTo && (
        <Tooltip title={formatMessage(appMessages.back)}>
          <IconButton
            {...qaProp(qa, '__back')}
            aria-label={formatMessage(appMessages.back)}
            data-testid="breadcrumb-back"
            component={RouterLink}
            size="small"
            sx={{ flexShrink: 0 }}
            to={backTo}
          >
            <Iconify icon="material-symbols:arrow-back" size="small" />
          </IconButton>
        </Tooltip>
      )}
      <MuiBreadcrumbs
        {...qaProp(qa)}
        aria-label={formatMessage(messages.title)}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          '& ol': { flexWrap: 'nowrap' },
          '& li': { minWidth: 0 },
        }}
      >
        <Link
          {...qaProp(qa, '__link-home')}
          className="Mfw-Breadcrumb-item"
          data-testid="breadcrumb-home"
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/"
        >
          {formatMessage(messages.home)}
        </Link>
        {renderedCrumbs.map((value, index) => {
          const last = index === lastPathIndex;
          const to = value.to;

          const isInvalidTitle = isTemplateTitle(value.title);

          if (isInvalidTitle)
            return <Skeleton width={80} key={value.pathname} />;

          return last || to === false ? (
            <Typography
              className="Mfw-Breadcrumb-item"
              {...qaProp(qa, last ? '__link-current' : '__text')}
              data-testid={last ? 'breadcrumb-current' : 'breadcrumb-text'}
              key={value.pathname}
            >
              {routeMessages[value.title as RoutesMessagesDescriptor]
                ? formatMessage(
                    routeMessages[value.title as RoutesMessagesDescriptor],
                  )
                : value.title}
            </Typography>
          ) : (
            <Link
              {...qaProp(qa, `__link-${to}`)}
              data-testid={`breadcrumb-${value.title}`}
              className="Mfw-Breadcrumb-item"
              component={RouterLink}
              underline="hover"
              color="inherit"
              key={value.pathname}
              to={to}
            >
              {routeMessages[value.title as RoutesMessagesDescriptor]
                ? formatMessage(
                    routeMessages[value.title as RoutesMessagesDescriptor],
                  )
                : value.title}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default React.memo(Breadcrumbs);
